// Banneritekstien tiiviste suostumuksen todistusketjuun (V2-T9).
//
// Vaiheissa 0-1 käytettiin heikkoa 32-bittistä tekstihashia: se riittää
// muutoksen havaitsemiseen mutta ei todisteeksi siitä, mitä kävijälle
// näytettiin — 32 bitissä törmäyksen voi tuottaa tarkoituksella. Nyt hash on
// SHA-256 (Web Crypto), ja hash-tyyppi tallennetaan riville, jotta vanhat ja
// uudet rivit ovat erotettavissa (append-only-loki: vanhoja ei muuteta).
//
// HUOM (tietoinen duplikaatti): sama logiikka on myös
// supabase/functions/_shared/text-hash.ts, koska kävijäpolun paketti ja
// edge-funktiot deployataan eri putkilla eivätkä voi importata toisiltaan.
// text-hash.test.ts vertaa molempia toteutuksia ristiin, joten ne eivät voi
// erkaantua huomaamatta.

export const HASH_ALGO_SHA256 = 'sha256';
export const HASH_ALGO_LEGACY32 = 'legacy32';

/**
 * Kanoninen merkkijono banneritekstien tiivistettä varten: avaimet
 * lajitellaan, jotta tulos ei riipu siitä, missä järjestyksessä tietokanta tai
 * JSON-jäsennys palauttaa kentät. Ilman lajittelua kävijän selaimessa ja
 * palvelimella laskettu hash voisi erota vaikka teksti on sama.
 */
export function canonicalTextsString(texts: unknown): string {
  return JSON.stringify(canonicalize(texts));
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const src = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(src).sort()) {
      if (src[key] === undefined) continue;
      out[key] = canonicalize(src[key]);
    }
    return out;
  }
  return value;
}

/** SHA-256 heksadesimaalina (Web Crypto; sama API selaimessa ja Denossa). */
export async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const view = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < view.length; i++) {
    hex += view[i].toString(16).padStart(2, '0');
  }
  return hex;
}

/**
 * Vaiheiden 0-1 32-bittinen hash. Säilytetään vain varapolkuna ympäristöihin,
 * joissa Web Crypto ei ole käytettävissä (esim. suojaamaton http-yhteys):
 * silloin on parempi kirjata heikko hash tyyppimerkinnällä kuin ei mitään.
 */
export function legacy32Hash(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(16);
}

export interface TextHashResult {
  hash: string;
  algo: string;
}

/**
 * Banneritekstien tiiviste ja käytetty algoritmi. Käyttää SHA-256:ta, kun Web
 * Crypto on käytettävissä; muuten varapolkua.
 */
export async function bannerTextHash(texts: unknown): Promise<TextHashResult> {
  const canonical = canonicalTextsString(texts);
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      return { hash: await sha256Hex(canonical), algo: HASH_ALGO_SHA256 };
    }
  } catch {
    // Web Crypto ei käytettävissä (esim. suojaamaton yhteys) → varapolku
  }
  return { hash: legacy32Hash(canonical), algo: HASH_ALGO_LEGACY32 };
}
